import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

interface Review {
  text: string;
  name: string;
}

const heroImages = [
  "/images/TruckImage.webp",
  "/images/PitBeef.webp",
  "/images/Ribs.webp",
  "/images/MotherClucker.webp",
  "/images/Shrimp.webp",
  "/images/Nachos.webp",
];

const neonColors = ["#fc03f4", "#03f0fc", "#fc03f4", "#03f0fc", "#fc03f4"];

export default function HomePage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);

  // Fetch reviews
  useEffect(() => {
    fetch("/reviews.json")
      .then((res) => res.json())
      .then((data) => setReviews(data.reviews))
      .catch((err) => console.error("Error loading reviews:", err));
  }, []);

  // Image slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document
      .querySelectorAll(
        ".animate-fade-in-left, .animate-fade-in-right, .animate-fade-in-up, .feature-card",
      )
      .forEach((el) => {
        observerRef.current?.observe(el);
      });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const localBusinessSchema = useMemo(() => {
    const siteUrl = window.location.origin;

    return {
      "@context": "https://schema.org",
      "@type": "FoodTruck",
      name: "Bull on the Loose Food Truck",
      url: siteUrl,
      email: "BullOnTheLooseLLC@gmail.com",
      telephone: "+1-443-687-2020",
      image: [
        `${siteUrl}/images/TruckImage.webp`,
        `${siteUrl}/images/BOTLLogo.webp`,
      ],
      sameAs: [
        "https://www.facebook.com/BullOnTheLoose",
        "https://www.instagram.com/bullontheloose1",
      ],
      servesCuisine: ["Barbecue", "American"],
      areaServed: ["Howard County, MD", "Columbia, MD", "Ellicott City, MD"],
      priceRange: "$$",
    };
  }, []);

  return (
    <>
      <Seo
        title="Fresh BBQ Food Truck in Howard County, MD"
        description="Bull on the Loose serves pit beef, BBQ, ribs, and more across Howard County and surrounding Maryland areas. View menu, events, and catering."
        path="/"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <section className="min-h-screen relative pt-[70px] overflow-hidden">
        {/* Image Slideshow Background */}
        <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <div
              key={img}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
        </div>

        {/* Content - Left aligned */}
        <div className="relative z-10 min-h-[calc(100vh-70px)] flex flex-col justify-center px-6 md:px-12 lg:px-20 max-w-[700px]">
          <span className="text-accent-blue text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Bull on the Loose
          </span>
          <h1 className="text-[clamp(3rem,10vw,6rem)] leading-[0.95] font-display font-bold text-white mb-6">
            <GradientText colors={neonColors} animationSpeed={6}>
              {" "}
              We Bring the BBQ to You!
            </GradientText>
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-[500px] mb-10 leading-relaxed">
            Fresh pit beef, smoky BBQ, and bold flavors made to order. Find us
            around town or order ahead.
          </p>

          {/* Buttons */}
          <div className="flex gap-4 flex-wrap">
            <Link to="/menu" className="btn btn-primary btn-large">
              <span></span> View Menu
            </Link>
            <Link
              to="/events"
              className="btn btn-largxe bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white hover:text-bg-primary"
            >
              Events
            </Link>
          </div>

          {/* Slide indicators */}
          <div className="flex gap-2 mt-12">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? "w-8 bg-accent-pink"
                    : "w-4 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-[120px] relative overflow-hidden">
        <div className="absolute inset-0 -z-10 intro-section-bg intro-pattern-bg"></div>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10">
            {/* Intro Content */}
            <div className="animate-fade-in-left" id="intro-content">
              <span className="section-label">About Us</span>
              <GradientText
                colors={neonColors}
                animationSpeed={6}
                className="section-title"
              >
                Welcome to the Stampede!
              </GradientText>
              <p className="text-lg text-text-secondary mb-5 leading-relaxed">
                Inspired by the over 40-year legacy of our sister company, The
                Canopy in Ellicott City, Maryland, we're unleashing a new
                culinary adventure: "Bull on the Loose!" This name captures our
                spirit while honoring the family tradition. We're hitting the
                road to serve Howard County and surrounding areas, bringing the
                same love and quality of food you expect, now on wheels. Can you
                help us lasso the Bull?
              </p>
              <p className="text-lg text-text-secondary mb-5 leading-relaxed">
                From our signature Bodacious Pit Beef to our slow-smoked BBQ
                ribs, every dish is crafted with love and the finest
                ingredients.
              </p>

              {/* Features */}
              <div className="flex flex-col gap-6 mt-10">
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.1s" }}
                >
                  <div className="text-[2rem] w-[60px] h-[60px] flex items-center justify-center bg-bg-elevated rounded-xl flex-shrink-0">
                    🔥
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Fresh & Fast
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Made to order with the freshest ingredients
                    </p>
                    <Link
                      to="/menu"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      View Menu
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.2s" }}
                >
                  <div className="text-[2rem] w-[60px] h-[60px] flex items-center justify-center bg-bg-elevated rounded-xl flex-shrink-0">
                    📍
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Find Us Around Town
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Check our socials for daily locations
                    </p>
                    <Link
                      to="/events"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      See Events
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
                <div
                  className="animate-fade-in-left feature-card flex gap-5 items-start p-5 bg-bg-card rounded-xl border border-border-default transition-all duration-500 hover:border-accent-purple hover:translate-x-2"
                  style={{ transitionDelay: "0.3s" }}
                >
                  <div className="text-[2rem] w-[60px] h-[60px] flex items-center justify-center bg-bg-elevated rounded-xl flex-shrink-0">
                    🎉
                  </div>
                  <div className="flex-1">
                    <h3 className="font-body font-semibold text-text-primary mb-1 text-lg">
                      Events & Catering
                    </h3>
                    <p className="text-text-secondary text-[0.95rem] mb-3">
                      Book us for your next party or event
                    </p>
                    <Link
                      to="/catering"
                      className="inline-flex items-center gap-1 text-sm font-medium text-accent-pink hover:text-accent-pink-light transition-colors"
                    >
                      Learn More
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews Showcase */}
            <div className="animate-fade-in-right" id="intro-visual">
              <p className="text-sm font-semibold uppercase tracking-[0.15em] text-accent-pink mb-4">
                What People Are Saying:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reviews.slice(0, 4).map((review, index) => (
                  <div
                    key={index}
                    className={`review-card relative bg-bg-card rounded-[20px] p-6 border border-border-default transition-all duration-400 overflow-hidden animate-float-${
                      index + 1
                    } group hover:-translate-y-2 hover:border-accent-pink hover:shadow-lg-custom hover:shadow-glow-pink`}
                  >
                    {/* Quote icon */}
                    <svg
                      className="w-8 h-8 text-accent-pink/30 mb-3"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                    <p className="text-text-secondary text-[0.95rem] leading-relaxed mb-4 italic">
                      "{review.text}"
                    </p>
                    <p className="font-display font-semibold text-accent-blue text-sm">
                      — {review.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facebook Section */}
      <section className="py-[100px] bg-bg-primary">
        <div className="container">
          <span className="section-label block text-center">
            Stay Connected
          </span>
          <div className="flex justify-center mb-4">
            <GradientText
              colors={neonColors}
              animationSpeed={6}
              className="section-title text-center"
            >
              Latest Updates
            </GradientText>
          </div>
          <p className="section-subtitle">
            Follow us on Facebook to stay up-to-date on upcoming events!
          </p>

          {/* Elfsight Facebook Feed */}
          <div className="max-w-[1000px] mx-auto">
            <div
              className="elfsight-app-a5ea234f-8a91-4ee5-8a3c-4e2e57b518c3"
              data-elfsight-app-lazy
            ></div>
          </div>
        </div>
      </section>
    </>
  );
}
